<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="!form.isDirty"
        :primary-button-label="!sla ? trans('general.action.add') : trans('general.action.save')"
        :processing="form.processing"
        :title="title"
        max-width="4xl"
        @submit="submitForm"
    >
        <form class="mb-3 space-y-6" @submit.prevent="submitForm">
            <service-level-agreement-header-fields v-model="form" :asset-types="assetTypes.data" :contracting-authorities="contractingAuthorities" />

            <sla-affected-assets-section v-if="sla && affectedAssets" :assets="affectedAssets.data ?? []" />

            <maintainer-business-contacts-section :business-contacts="businessContacts" @add="addBusinessContact" @edit="editBusinessContacts" />

            <maintainer-agreement-term-section :model-value="form" />

            <maintainer-sla-support-schedule-section
                v-model="form.supportSchedules"
                :duration-units="durationUnits.data"
                :errors="form.errors"
                :support-schedule-defaults="defaultAgreement?.supportScheduleDefaults"
                :support-contacts="supportContacts"
                :supported-channel-types="supportedChannelTypes"
                :timezones="timezones.data"
                :weekdays="weekdays.data"
                @add-support-contact="addSupportContact"
            />

            <service-level-agreement-form
                v-model="form"
                :attachments="attachments ?? []"
                @updated-attachment="onAttachmentUpdated"
                @deleted-attachment="onAttachmentDeleted"
            />
        </form>

        <required-fields />
    </Modal>
</template>

<script setup>
import ServiceLevelAgreementForm from '@/Pages/Stakeholders/Maintainers/ServiceLevelAgreementForm.vue';
import ServiceLevelAgreementHeaderFields from '@/Pages/Stakeholders/Maintainers/ServiceLevelAgreementHeaderFields.vue';
import MaintainerBusinessContactsSection from '@/Pages/Stakeholders/Maintainers/MaintainerBusinessContactsSection.vue';
import MaintainerSlaSupportScheduleSection from '@/Pages/Stakeholders/Maintainers/MaintainerSlaSupportScheduleSection.vue';
import RequiredFields from '@/Components/RequiredFields.vue';
import { trans } from '@/mixins/i18n.js';
import { visitModal } from '@/Utils/visitModal';
import { useForm } from '@inertiajs/vue3';
import { computed, ref } from 'vue';
import { normalizeNumber } from '@/Utils/normalizeNumber.js';
import Modal from '@/Components/Modal.vue';
import MaintainerAgreementTermSection from '@/Pages/Stakeholders/Maintainers/MaintainerAgreementTermSection.vue';
import SlaAffectedAssetsSection from '@/Pages/Stakeholders/Maintainers/SlaAffectedAssetsSection.vue';
import { normalizeSupportSchedules, resolveDefaultSupportScheduleTimezone } from '@/Pages/Stakeholders/Maintainers/supportSchedules';

defineEmits(['created', 'updated']);

const modalRef = ref(null);

const props = defineProps({
    maintainer: Object,
    defaultAgreement: Object,
    assetTypes: Object,
    contractingAuthorities: Object,
    durationUnits: {
        type: Object,
        default: () => ({ data: [] }),
    },
    attachments: Array,
    sla: Object,
    timezones: {
        type: Object,
        default: () => ({ data: [] }),
    },
    weekdays: {
        type: Object,
        default: () => ({ data: [] }),
    },
    supportedChannelTypes: {
        type: Array,
        default: () => [],
    },
    affectedAssets: {
        type: Object,
        default: null,
    },
});

const transformExpectedUptimeDaily = (value) => {
    if (!value) return null;
    return [value.start, value.end];
};

const form = useForm({
    type: props.sla?.type?.value,
    contractingAuthorityId: props.sla?.contractingAuthority?.id,
    startDate: props.sla?.startDate,
    endDate: props.sla?.endDate,
    attachments: [],
    expectedUptimeDaily: transformExpectedUptimeDaily(props.sla?.expectedUptimeDaily),
    expectedUptimeMonthlyPercentage: props.sla?.expectedUptimeMonthlyPercentage?.toString(),
    supportSchedules: normalizeSupportSchedules(
        props.sla?.supportSchedules ?? [],
        resolveDefaultSupportScheduleTimezone(props.timezones?.data ?? []),
        props.defaultAgreement?.supportScheduleDefaults,
    ),
});

const title = computed(() => (props.sla ? trans('maintainers.sla.edit.title') : trans('maintainers.sla.create.title')));
const attachments = ref(props.sla?.attachments ?? []);
const businessContacts = computed(() => props.maintainer?.businessContacts ?? []);
const supportContacts = computed(() => props.maintainer?.supportContacts ?? []);

const contractingAuthorities = computed(() => {
    return (props.contractingAuthorities?.data || []).map((el) => ({
        label: el.name,
        value: el.id,
    }));
});

const submitForm = () => {
    const url = !!props.sla
        ? route('maintainers.slas.update', {
              maintainer: props.maintainer,
              sla: props.sla,
          })
        : route('maintainers.slas.store', {
              maintainer: props.maintainer,
          });

    form.transform((data) => {
        const payload = { ...data };

        if (!!props.sla) {
            payload._method = 'PUT';
        }

        payload.expectedUptimeDaily = reverseExpectedUptimeDaily(payload.expectedUptimeDaily);
        payload.expectedUptimeMonthlyPercentage = normalizeNumber(payload.expectedUptimeMonthlyPercentage);

        if (isNaN(payload.expectedUptimeMonthlyPercentage)) {
            payload.expectedUptimeMonthlyPercentage = null;
        }

        return payload;
    }).post(url, {
        preserveScroll: true,
        onSuccess: () => {
            modalRef.value?.close();
        },
    });
};

const onAttachmentUpdated = (attachment) => {
    attachments.value = attachments.value.map((el) => {
        if (el.id === attachment.id) {
            return attachment;
        }
        return el;
    });
};

const onAttachmentDeleted = (attachment) => {
    attachments.value = attachments.value.filter((el) => el.id !== attachment.id);
};

const openMaintainerModal = () => {
    const url = props.maintainer?.id ? route('maintainers.edit', props.maintainer.id) : route('maintainers.create');

    visitModal(url, {
        preserveScroll: true,
        onAfterLeave: () => {
            modalRef.value?.reload();
        },
    });
};

const addBusinessContact = () => openMaintainerModal();
const editBusinessContacts = () => openMaintainerModal();
const addSupportContact = () => openMaintainerModal();

const reverseExpectedUptimeDaily = (value) => {
    if (!value) return null;
    return {
        start: value[0],
        end: value[1],
    };
};
</script>
